import { Feedback, Field, Form, Input, Label } from '@availity/form';
import { DocumentDetails } from '../../../../slices/marketplace/timeline/models/document-details.model';
import {
    defaultTimelineAction,
    TimelineAction,
} from '../../../../slices/marketplace/timeline/models/timeline-actions.model';
import './BuyerSelectionForm.scss';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import {
    BuyerSelectionOptionsList,
    TimelineActionType,
} from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import * as yup from 'yup';
import createNotification from '../../../../utils/createNotification';
import { MarketplaceDocumentSectionFieldValidator } from '../DocumentSectionField/MarketplaceDocumentSectionFieldValidator';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { DocumentSection } from '../../../../slices/marketplace/timeline/models/document-section.model';
import Helpers from '../../../../utils/helper';
import { DocumentDetailsStatus } from '../../../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import { ActionDocumentType } from '../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import { EngagementDocumentSource } from '../../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import { MarketplaceDocumentSectionField } from '../DocumentSectionField/MarketplaceDocumentSectionField';
import { useStateSelector } from '../../../../store/selectors';
import { DocumentDetailsContentType } from '../../../../slices/marketplace/timeline/models/enums/document-details-content-type';

interface BuyerSelectionFormProps {
    party: TimelineParty;
    documentsSections: DocumentSection[];
    onSubmit(action: TimelineAction, documentDetails?: DocumentDetails): void;
    disabled: boolean;
    currentEvent: TimelineEvent;
    linkedAction: TimelineAction;
    timelineEvents: TimelineEvent[];
    getDocumentDetails(): DocumentDetails | null;
    setDocumentSectionList: (documentSections: DocumentSection[]) => void;
    documentDetailsList: DocumentDetails[];
}
const BuyerSelectionForm = forwardRef((props: BuyerSelectionFormProps, ref) => {
    let modalForm: any = {};
    const documentTemplates = useStateSelector(
        (state) => state.timelines.documentTemplatesTable.templates
    );

    useImperativeHandle(ref, () => ({
        handleSubmit() {
            modalForm?.handleSubmit();
        },
    }));

    const getDefaultDocumentDetails = () => {
        const defaultDocDetails: DocumentDetails = {
            id: Helpers.generateGUID(),
            name: '',
            status: DocumentDetailsStatus.RequiresAction,
            counterParty: TimelineParty.VetValue,
            sectionId: '',
            type: ActionDocumentType.IdNda,
            isUnregisteredFile: true,
            source: EngagementDocumentSource.Template,
            templateId: 0,
            fileName: '',
            contentType: DocumentDetailsContentType.General
        };

        return defaultDocDetails;
    };

    const [documentDetails] = useState({
        ...(props.getDocumentDetails() ?? getDefaultDocumentDetails()),
    });

    return (
        <div className="buyer-selection-form">
            <Form
                innerRef={(c) => {
                    modalForm = c;
                }}
                initialValues={{
                    actionType: BuyerSelectionOptionsList.some(
                        (s) => s.value === props.linkedAction?.actionType
                    )
                        ? props.linkedAction?.actionType
                        : TimelineActionType.SellerBuyerSelectionIdNda,

                    documentTemplate: documentDetails.templateId,
                    documentName: documentDetails.name,
                    documentSection: documentDetails.sectionId,
                }}
                validationSchema={yup.object().shape({
                    actionType: yup
                        .number()
                        .uniqueGroupValidation(
                            props.timelineEvents ?? [],
                            props.currentEvent
                        )
                        .uniqueActionValidation(
                            props.timelineEvents ?? [],
                            props.currentEvent
                        ),
                    documentTemplate: yup.number().when('actionType', {
                        is: (val: any) =>
                            Number(val) ===
                            TimelineActionType.SellerBuyerSelectionIdNda,
                        then: (schema) =>
                            schema
                                .required('Template is required')
                                .min(1, 'Template is required'),
                        otherwise: (schema) => schema.notRequired(),
                    }),
                    documentSection:
                        MarketplaceDocumentSectionFieldValidator.documentSectionValidationRules(
                            props.documentsSections,
                            props.party
                        ).when('actionType', {
                            is: (val: any) =>
                                Number(val) ===
                                TimelineActionType.SellerBuyerSelectionIdNda,
                            then: (schema) =>
                                schema.required('Section is required'),
                            otherwise: (schema) => schema.notRequired(),
                        }),
                    documentName: yup
                        .string()
                        .trim()
                        .when('actionType', {
                            is: (val: any) =>
                                Number(val) ===
                                TimelineActionType.SellerBuyerSelectionIdNda,
                            then: (schema) =>
                                schema.required('Document name is required'),
                            otherwise: (schema) => schema.notRequired(),
                        }),
                })}
                onSubmit={(values) => {
                    if (
                        props.linkedAction?.actionType ===
                        TimelineActionType.SellerEngagementDocument
                    ) {
                        const indexOfEventWithRefToThisAction =
                            props.timelineEvents.findIndex(
                                (e) =>
                                    e.linkedAction?.actionReferenceId ===
                                    props.linkedAction?.id
                            );
                        if (indexOfEventWithRefToThisAction > -1) {
                            createNotification(
                                `You can't change the Action type because it is used in the event with index ${
                                    indexOfEventWithRefToThisAction + 1
                                }.`,
                                'error'
                            );
                            return;
                        }
                    }
                    if (
                        Number(values.actionType) ===
                        TimelineActionType.SellerBuyerSelectionIdNda
                    ) {
                        let docDetails = documentDetails;
                        docDetails = {
                            ...docDetails,
                            name: values.documentName.trim(),
                            type: ActionDocumentType.IdNda,
                            templateId: values.documentTemplate,
                            counterParty: TimelineParty.VetValue,
                            sectionId: values.documentSection,
                            source: EngagementDocumentSource.Template,
                        };
                        props.onSubmit(
                            {
                                ...defaultTimelineAction,
                                actionType: Number(
                                    TimelineActionType.SellerBuyerSelectionIdNda
                                ),
                                referenceItemId: docDetails.id,
                            },
                            docDetails
                        );
                    } else {
                        props.onSubmit({
                            ...defaultTimelineAction,
                            actionType: Number(values.actionType),
                        });
                    }
                }}>
                {({ values, errors }) => {
                    return (
                        <>
                            <Input
                                type="select"
                                name="actionType"
                                disabled={props.disabled}>
                                {BuyerSelectionOptionsList.map((option) => (
                                    <option
                                        key={option.value}
                                        value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Input>
                            <Feedback name="actionType" />
                            {Number(values.actionType) ===
                            TimelineActionType.SellerBuyerSelectionIdNda ? (
                                <>
                                    <Label for="documentTemplate">
                                        Document Template
                                    </Label>
                                    <Input
                                        type="select"
                                        name="documentTemplate"
                                        disabled={props.disabled}>
                                        <option value={0}>Select</option>
                                        {documentTemplates.map((option) => (
                                            <option
                                                key={`template-${option.id}`}
                                                value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <Feedback name="documentTemplate" />
                                    <MarketplaceDocumentSectionField
                                        documentsSections={
                                            props.documentsSections
                                        }
                                        disabled={props.disabled}
                                        setDocumentSectionList={
                                            props.setDocumentSectionList
                                        }
                                        documentDetailsList={
                                            props.documentDetailsList
                                        }
                                        type={
                                            'buyers'
                                        }></MarketplaceDocumentSectionField>
                                    <Field
                                        className="field"
                                        name="documentName"
                                        label="Document Name"
                                        disabled={props.disabled}
                                    />
                                </>
                            ) : (
                                []
                            )}
                        </>
                    );
                }}
            </Form>
        </div>
    );
});

export default BuyerSelectionForm;
